import { stripUnderlineTags } from "../../shared/citations/ai/parseUnderlineTags";
import type {
  CellUpdate,
  PresentationModification,
  ShapeModification,
  Snippet,
  SnippetItem,
  TableData,
} from "../types";

export interface StripExtractResult {
  cleanText: string;
  refs: string[];
}

export function stripAndExtract(input: string): StripExtractResult {
  if (typeof input !== "string" || input.length === 0) {
    return { cleanText: input ?? "", refs: [] };
  }
  const { cleanText, refs } = stripUnderlineTags(input);
  return { cleanText, refs: refs.map((ref) => ref.id) };
}

export interface SlideCitationRefs {
  slideKey: string;
  slideId?: string;
  slideIndex: number;
  refsByShape: Record<string, string[]>;
}

export interface StripModificationsResult {
  bySlide: SlideCitationRefs[];
  allCitationIds: string[];
}

function ensureRefsBucket(
  bySlide: SlideCitationRefs[],
  slideKeyToBucket: Map<string, SlideCitationRefs>,
  slideId: string | undefined,
  slideIndex: number
): SlideCitationRefs {
  const slideKey = slideId && slideId.length > 0 ? slideId : `index:${slideIndex}`;
  const existing = slideKeyToBucket.get(slideKey);
  if (existing) return existing;
  const bucket: SlideCitationRefs = {
    slideKey,
    slideId,
    slideIndex,
    refsByShape: {},
  };
  slideKeyToBucket.set(slideKey, bucket);
  bySlide.push(bucket);
  return bucket;
}

function pushRefs(bucket: SlideCitationRefs, shapeId: string, ids: string[]): void {
  if (!ids.length) return;
  const list = (bucket.refsByShape[shapeId] = bucket.refsByShape[shapeId] || []);
  for (const id of ids) {
    if (id && list.indexOf(id) === -1) list.push(id);
  }
}

function stripStringInPlace<T extends Record<string, unknown>>(
  obj: T,
  field: keyof T,
  shapeId: string,
  bucket: SlideCitationRefs,
  allIds: Set<string>
): void {
  const value = obj[field];
  if (typeof value !== "string" || value.length === 0) return;
  const { cleanText, refs } = stripAndExtract(value);
  if (refs.length === 0) {
    if (cleanText !== value) {
      (obj as Record<string, unknown>)[field as string] = cleanText;
    }
    return;
  }
  (obj as Record<string, unknown>)[field as string] = cleanText;
  pushRefs(bucket, shapeId, refs);
  for (const id of refs) allIds.add(id);
}

function stripCellUpdates(
  updates: CellUpdate[] | undefined,
  shapeId: string,
  bucket: SlideCitationRefs,
  allIds: Set<string>
): void {
  if (!Array.isArray(updates)) return;
  for (const update of updates) {
    stripStringInPlace(update as unknown as Record<string, unknown>, "text", shapeId, bucket, allIds);
  }
}

function stripTableRows(
  table: TableData | undefined,
  shapeId: string,
  bucket: SlideCitationRefs,
  allIds: Set<string>
): void {
  if (!table || !Array.isArray(table.rows)) return;
  for (let r = 0; r < table.rows.length; r++) {
    const row = table.rows[r];
    if (!Array.isArray(row)) continue;
    for (let c = 0; c < row.length; c++) {
      const cell = row[c];
      if (typeof cell !== "string" || cell.length === 0) continue;
      const { cleanText, refs } = stripAndExtract(cell);
      if (refs.length === 0) {
        if (cleanText !== cell) row[c] = cleanText;
        continue;
      }
      row[c] = cleanText;
      pushRefs(bucket, shapeId, refs);
      for (const id of refs) allIds.add(id);
    }
  }
}

function stripSnippet(
  snippet: Snippet | undefined,
  snippetShapeId: string,
  bucket: SlideCitationRefs,
  allIds: Set<string>
): void {
  if (!snippet) return;
  stripStringInPlace(snippet as unknown as Record<string, unknown>, "title", snippetShapeId, bucket, allIds);
  if (Array.isArray(snippet.items)) {
    for (let i = 0; i < snippet.items.length; i++) {
      const item = snippet.items[i] as SnippetItem;
      stripStringInPlace(item as unknown as Record<string, unknown>, "label", snippetShapeId, bucket, allIds);
      stripStringInPlace(item as unknown as Record<string, unknown>, "description", snippetShapeId, bucket, allIds);
      if (Array.isArray(item.bullets)) {
        for (let b = 0; b < item.bullets.length; b++) {
          const bullet = item.bullets[b];
          if (typeof bullet !== "string" || bullet.length === 0) continue;
          const { cleanText, refs } = stripAndExtract(bullet);
          if (refs.length === 0) {
            if (cleanText !== bullet) item.bullets[b] = cleanText;
            continue;
          }
          item.bullets[b] = cleanText;
          pushRefs(bucket, snippetShapeId, refs);
          for (const id of refs) allIds.add(id);
        }
      }
    }
  }
}

function stripShape(
  shape: ShapeModification,
  bucket: SlideCitationRefs,
  allIds: Set<string>
): void {
  const shapeId = shape.id;
  stripStringInPlace(shape as unknown as Record<string, unknown>, "text", shapeId, bucket, allIds);
  if (Array.isArray(shape.textReplacements)) {
    for (const replacement of shape.textReplacements) {
      stripStringInPlace(
        replacement as unknown as Record<string, unknown>,
        "replace",
        shapeId,
        bucket,
        allIds
      );
    }
  }
  stripTableRows(shape.table, shapeId, bucket, allIds);
  stripCellUpdates(shape.table?.textUpdates, shapeId, bucket, allIds);
  stripCellUpdates(shape.cellUpdates, shapeId, bucket, allIds);
}

export function stripModificationsInPlace(
  modifications: PresentationModification
): StripModificationsResult {
  const bySlide: SlideCitationRefs[] = [];
  const slideKeyToBucket = new Map<string, SlideCitationRefs>();
  const allIds = new Set<string>();

  if (!modifications || !Array.isArray(modifications.slides)) {
    return { bySlide, allCitationIds: [] };
  }

  for (let s = 0; s < modifications.slides.length; s++) {
    const slide = modifications.slides[s];
    if (!slide) continue;
    const bucket = ensureRefsBucket(bySlide, slideKeyToBucket, slide.id, slide.index);
    const shapes = Array.isArray(slide.shapes) ? slide.shapes : [];
    for (let sh = 0; sh < shapes.length; sh++) {
      stripShape(shapes[sh], bucket, allIds);
    }
    if (Array.isArray(slide.snippets)) {
      for (let i = 0; i < slide.snippets.length; i++) {
        stripSnippet(slide.snippets[i], `snippet:${slide.id ?? slide.index}:${i}`, bucket, allIds);
      }
    }
    if (slide.compositeLayout) {
      stripSnippet(slide.compositeLayout, `composite:${slide.id ?? slide.index}`, bucket, allIds);
    }
    if (Object.keys(bucket.refsByShape).length === 0) {
      const idx = bySlide.indexOf(bucket);
      if (idx >= 0) bySlide.splice(idx, 1);
      slideKeyToBucket.delete(bucket.slideKey);
    }
  }

  const allCitationIds: string[] = [];
  allIds.forEach((id) => allCitationIds.push(id));
  return { bySlide, allCitationIds };
}
